import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VideoPlayerProvider } from './context/VideoPlayerContext';
import { Toaster } from './components/ui/toaster';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import CreatorPage from './pages/CreatorPage';
import WhatYouCanDoPage from './pages/WhatYouCanDoPage';
import PrivateProfilePage from './pages/PrivateProfilePage';
import CreatorPrototypePage from './pages/CreatorPrototypePage';
import ExplorePage from './pages/ExplorePage';
import CreatePage from './pages/CreatePage';
import ProfilePage from './pages/ProfilePage';
import CheckoutPage from './pages/CheckoutPage';
import WalletPage from './pages/WalletPage';
import AdminPage from './pages/AdminPage';
import PresencePing from './components/PresencePing';
import AvatarRequiredGate from './components/AvatarRequiredGate';
import ProfileCompletionGate from './components/ProfileCompletionGate';
import AgeGate from './components/AgeGate';
import AdminO2ODashboard from './pages/admin/AdminO2ODashboard';
import AdminWalletPackages from './pages/admin/AdminWalletPackages';
import LivesPage from './pages/LivesPage';
import LiveDashboardPage from './pages/LiveDashboardPage';
import LiveDetailPage from './pages/LiveDetailPage';
import UserProfilePage from './pages/UserProfilePage';
import MessagesPage from './pages/MessagesPage';
import ShopPage from './pages/ShopPage';
import SettingsPage from './pages/SettingsPage';
import CreatorOnboardingWizard from './pages/CreatorOnboardingWizard';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SocialCompletePage from './pages/SocialCompletePage';
import JoinCommunityPage from './pages/JoinCommunityPage';
import CallLandingPage from './pages/CallLandingPage';
import CallsAvailabilityPage from './pages/CallsAvailabilityPage';
import O2OReturnPage from './pages/O2OReturnPage';
import O2OCallPage from './pages/O2OCallPage';
import IncomingCallListener from './components/o2ocall/IncomingCallListener';
import WaitingRoomPage from './pages/WaitingRoomPage';
import PausedScreen from './pages/PausedScreen';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiesPage from './pages/CookiesPage';
import CommunityRulesPage from './pages/CommunityRulesPage';
import ChildSafetyPage from './pages/ChildSafetyPage';
import FaqPage from './pages/FaqPage';
import ContactPage from './pages/ContactPage';
import HelpCenterPage from './pages/HelpCenterPage';
import FavoritesPage from './pages/FavoritesPage';
import PostDetailPage from './pages/PostDetailPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import SideNav from './components/SideNav';
import BottomNav from './components/BottomNav';
import TopBar from './components/TopBar';
import EmailVerifyBanner from './components/EmailVerifyBanner';
import StandbyBanner from './components/StandbyBanner';
import { CreatorLiveReminder } from './components/CreatorLiveReminder';
import CookieBanner from './components/CookieBanner';
import LegalFooter from './components/LegalFooter';
import PwaInstallBanner from './components/PwaInstallBanner';
import WhatsAppWidget from './components/WhatsAppWidget';
import UiUnlockWatchdog from './components/UiUnlockWatchdog';
import PageViewTracker from './components/PageViewTracker';
import { useAppOpenTracker } from './hooks/useAppOpenTracker';
import { initMediaTelemetry } from './lib/mediaTelemetry';

// Phase 47 · Fires a single PWA-vs-browser open-context ping per
// session. Must live inside <AuthProvider> so it can attach the JWT.
// Phase 51.1 · Also boots the media performance telemetry sampler.
const AppOpenTracker = () => {
  useAppOpenTracker();
  React.useEffect(() => { initMediaTelemetry(); }, []);
  return null;
};
import { useMetaPixel, PixelRouteTracker, PixelAdvancedMatching } from './hooks/useMetaPixel';
import AttributionCapture from './components/AttributionCapture';

const MetaPixelLoader = () => {
  useMetaPixel();
  return null;
};

const PublicRedirect = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    if (user.status === 'paused') return <Navigate to="/paused" replace />;
    // Phase 27.5 — unverified users must go through /verify-email first.
    if (user.email_verified === false) {
      return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email || '')}`} replace />;
    }
    return <Navigate to={user.is_admin ? '/admin' : '/'} replace />;
  }
  return children;
};

/**
 * Force logged-in users with status === 'paused' onto /paused.
 * Admins are exempt (they keep moderation access).
 */
const PausedGate = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user && user.status === 'paused' && !user.is_admin && location.pathname !== '/paused') {
    return <Navigate to="/paused" replace />;
  }
  return children;
};

const AppShell = ({ children }) => (
  <div className="min-h-screen bg-background text-foreground">
    <SideNav />
    <TopBar />
    <EmailVerifyBanner />
    <StandbyBanner />
    <div className="md:pl-[72px] xl:pl-[245px]">
      <CreatorLiveReminder />
      <div className="pb-24 md:pb-6">{children}</div>
      {/* Phase 44 · Feb 2026 — Legal footer visibile in fondo a tutte le
          pagine autenticate (FAQ, Contatti, Termini, Privacy, Cookie).
          Sostituisce le voci precedentemente presenti nel side/top menu. */}
      <div className="mx-auto max-w-4xl px-4 py-8">
        <LegalFooter />
      </div>
    </div>
    <BottomNav />
    {/* Phase 46 · PWA install prompt (Android + iOS fallback), globally mounted. */}
    <PwaInstallBanner />
  </div>
);

const Protected = ({ children }) => (
  <ProtectedRoute>
    <PausedGate>
      <AppShell>{children}</AppShell>
    </PausedGate>
  </ProtectedRoute>
);

// Checkout page uses its own layout (Stripe-like full screen)
const ProtectedRaw = ({ children }) => (
  <ProtectedRoute>
    <PausedGate>{children}</PausedGate>
  </ProtectedRoute>
);

// Paused screen — auth required, NO shell, NO PausedGate (would loop).
const PausedOnly = ({ children }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
);

// Admin-only route: requires authentication AND is_admin
const AdminOnly = ({ children }) => (
  <AdminRoute>
    <AppShell>{children}</AppShell>
  </AdminRoute>
);

/**
 * Phase 40 · Public/embedded hybrid route.
 * Legal & FAQ pages are reachable both by anonymous visitors AND
 * by authenticated users (e.g. via SideNav "Altro" menu). When
 * the user IS logged in, the page is wrapped in AppShell so the
 * standard navigation stays visible; otherwise it renders with its
 * own brand chrome via LegalPageLayout's standalone mode.
 */
const PublicOrShell = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    // Paused users still deserve the shell chrome (SideNav is hidden
    // for them anyway); use PausedGate to bounce them if needed.
    return (
      <PausedGate>
        <AppShell>{children}</AppShell>
      </PausedGate>
    );
  }
  return children;
};

/**
 * Root `/` behavior — public emotional landing for unauthenticated visitors,
 * feed HomePage for anyone already logged in (mirrors PublicRedirect's routing
 * so paused/unverified accounts still get bounced to the right screen).
 */
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <LandingPage />;
  if (user.status === 'paused') return <Navigate to="/paused" replace />;
  if (user.email_verified === false) {
    return (
      <Navigate
        to={`/verify-email?email=${encodeURIComponent(user.email || '')}`}
        replace
      />
    );
  }
  // Phase 86 fix — admins land on /admin after login (PublicRedirect), but
  // must still be able to browse the Home feed: no forced bounce here.
  return (
    <PausedGate>
      <AppShell>
        <HomePage />
      </AppShell>
    </PausedGate>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <VideoPlayerProvider>
        <BrowserRouter>
          <MetaPixelLoader />
          <PresencePing />
          <AttributionCapture />
          <PixelRouteTracker />
          {/* Phase 80 — Advanced Matching (em/external_id/ph) per utenti loggati. */}
          <PixelAdvancedMatching />
          <AppOpenTracker />
          {/* Phase F9.2 — Nymia is 18+ only: declared DOB gate + under-18
              total block. Mounted BEFORE the avatar gate (higher z-index). */}
          <AgeGate />
          {/* Phase F5.2 — new users must add a profile photo (legacy never gated) */}
          <AvatarRequiredGate />
          {/* Card Persone — every user without category and/or gender completes them on next access */}
          <ProfileCompletionGate />
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRedirect>
                  <LoginPage />
                </PublicRedirect>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRedirect>
                  <RegisterPage />
                </PublicRedirect>
              }
            />
            {/* Phase 27.5 — Mandatory email verification page (no auth gate;
                the user must be able to reach it before holding a token). */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* Phase 27.6 — Password reset (both pages are public). */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/social/complete" element={<SocialCompletePage />} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/creator" element={<CreatorPage />} />
            <Route path="/cosa-puoi-fare" element={<WhatYouCanDoPage />} />
            {/* Campaign-only landing (psicologi) — NOT linked in any menu. */}
            <Route path="/join-community" element={<JoinCommunityPage />} />
            <Route path="/call" element={<CallLandingPage />} />
            {/* Prototipo isolato — validazione feeling cinematografico (Scene 1-4) */}
            <Route path="/creator-prototype" element={<CreatorPrototypePage />} />
            <Route path="/explore" element={<Protected><ExplorePage /></Protected>} />
            <Route path="/create" element={<Protected><CreatePage /></Protected>} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/creator/onboarding" element={<Protected><CreatorOnboardingWizard /></Protected>} />
            <Route path="/wallet" element={<Protected><WalletPage /></Protected>} />
            <Route path="/admin" element={<AdminOnly><AdminPage /></AdminOnly>} />
            <Route path="/admin/o2o" element={<AdminOnly><AdminO2ODashboard /></AdminOnly>} />
            <Route path="/admin/wallet/packages" element={<AdminOnly><AdminWalletPackages /></AdminOnly>} />
            <Route path="/calls" element={<Protected><CallsAvailabilityPage /></Protected>} />
            <Route path="/lives" element={<Protected><LiveDashboardPage /></Protected>} />
            <Route path="/lives/discover" element={<Protected><LivesPage /></Protected>} />
            <Route path="/lives/:id" element={<LiveDetailPage />} />
            <Route path="/u/:username" element={<UserProfilePage />} />
            {/* Phase 54 — legacy /o2o/book removed (calendar booking archived).
                New instant pay-per-minute call room: */}
            <Route path="/call/:callId" element={<ProtectedRaw><O2OCallPage /></ProtectedRaw>} />
            <Route path="/o2o/return" element={<O2OReturnPage />} />
            <Route path="/session/:sessionId/waiting-room" element={<ProtectedRaw><WaitingRoomPage /></ProtectedRaw>} />
            <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
            <Route path="/shop" element={<Protected><ShopPage /></Protected>} />
            <Route path="/checkout/:sessionId" element={<ProtectedRaw><CheckoutPage /></ProtectedRaw>} />
            <Route path="/paused" element={<PausedOnly><PausedScreen /></PausedOnly>} />
            {/* Phase 40 — Legal & FAQ pages. Public but wrapped in AppShell
                automatically when the visitor is already logged in. */}
            <Route path="/terms" element={<PublicOrShell><TermsPage /></PublicOrShell>} />
            <Route path="/privacy" element={<PublicOrShell><PrivacyPage /></PublicOrShell>} />
            <Route path="/cookies" element={<PublicOrShell><CookiesPage /></PublicOrShell>} />
            <Route path="/community-rules" element={<PublicOrShell><CommunityRulesPage /></PublicOrShell>} />
            <Route path="/child-safety" element={<PublicOrShell><ChildSafetyPage /></PublicOrShell>} />
            <Route path="/faq" element={<Navigate to="/faq/users" replace />} />
            <Route path="/faq/users" element={<PublicOrShell><FaqPage audience="users" /></PublicOrShell>} />
            <Route path="/faq/creators" element={<PublicOrShell><FaqPage audience="creators" /></PublicOrShell>} />
            <Route path="/contact" element={<PublicOrShell><ContactPage /></PublicOrShell>} />
            <Route path="/help" element={<PublicOrShell><HelpCenterPage /></PublicOrShell>} />
            {/* Phase 50 · Favorites + Post detail (public + shared links) */}
            <Route path="/favorites" element={<Protected><FavoritesPage /></Protected>} />
            <Route path="/private-profile" element={<Protected><PrivateProfilePage /></Protected>} />
            <Route path="/post/:postId" element={<PublicOrShell><PostDetailPage /></PublicOrShell>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* Phase 48 · Global support chat — visible on every non-critical
              route for both anonymous and logged-in visitors. Lives INSIDE
              BrowserRouter because it depends on `useLocation`. */}
          <WhatsAppWidget />
          {/* Phase 54 · Global incoming-call listener (creator direct rings +
              waitlist auto-rings). Needs Router + Auth context. */}
          <IncomingCallListener />
          {/* Phase 76 · iOS anti-freeze: clears orphaned dialog locks. */}
          <UiUnlockWatchdog />
          {/* Phase 79 — campaign landing view counter (inside Router). */}
          <PageViewTracker />
        </BrowserRouter>
        <Toaster />
        <CookieBanner />
        </VideoPlayerProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
